export const ONE_ON_ONE_DELIVERY_VALUE = 'dating';
export const ONE_ON_ONE_LABEL = 'Creator Services';

export const GAME_TYPE_OPTIONS = [
  { value: 'standard', label: 'Standard', fullName: 'Standard', description: 'Highest number wins', definition: 'Choose a number higher than the current highest number. The highest number wins.' },
  { value: 'luba', label: 'LUN', fullName: 'Lowest Unique Number', description: 'Choose a lower number that no one else has picked', definition: 'Choose a lower number that no one else has picked. The lowest number picked exactly once wins.' },
  { value: 'huba', label: 'HUN', fullName: 'Highest Unique Number', description: 'Choose a higher number that no one else has picked', definition: 'Choose a higher number that no one else has picked. The highest number picked exactly once wins.' },
  { value: 'reveal_price', label: 'Reveal', fullName: 'Reveal the Price', description: 'Reveal the price', definition: 'Choose a number closest to the creator\'s hidden target price to win.' },
] as const;

// Present platform-generated copy under the current brand without changing
// persisted notification types, API values, or creator-authored content.
const matchCopyCase = (original: string, replacement: string): string => {
  if (original === original.toUpperCase()) return replacement.toUpperCase();
  if (original[0] === original[0].toUpperCase()) return replacement[0].toUpperCase() + replacement.slice(1);
  return replacement;
};

export const formatGameARamaText = (text: string): string =>
  text
    .replace(/\bhow do auctions work\b/gi, (value) => matchCopyCase(value, 'how does Game-a-Rama work'))
    .replace(/\bauctions allow you to bid on\b/gi, 'Game-a-Rama lets you play for')
    .replace(/\bauto[- ]bids?\b/gi, (value) => matchCopyCase(value, 'automatic play'))
    .replace(/\bwill bid\b/gi, (value) => matchCopyCase(value, 'will play'))
    .replace(/\ban(?=\s+auction\b)/gi, (article) => article[0] === 'A' ? 'A' : 'a')
    .replace(/\bauctions?\b/gi, 'Game-a-Rama')
    .replace(/\bluba\b/gi, 'LUN')
    .replace(/\bhuba\b/gi, 'HUN')
    .replace(/\bpersonal services?\b/gi, 'Creator Services')
    .replace(/\b(?:game cost|bid cost|cost per bid|per-bid fee)\b/gi, 'Cost to Play')
    .replace(/\b(?:entry|bid) history\b/gi, 'Play History')
    .replace(/\bbid threshold\b/gi, (value) => matchCopyCase(value, 'game goal'))
    .replace(/\bbidding is\b/gi, (value) => matchCopyCase(value, 'entries are'))
    .replace(/\b(?:place|placing|placed) (?:a |an |your )?bid\b/gi, (value) => {
      const verb = /^placing/i.test(value) ? 'submitting' : /^placed/i.test(value) ? 'submitted' : 'submit';
      return matchCopyCase(value, `${verb} ${/your/i.test(value) ? 'your' : 'an'} entry`);
    })
    .replace(/\bbid on\b/gi, (value) => matchCopyCase(value, 'enter'))
    .replace(/\b(to|can|cannot) bid\b/gi, (value, prefix: string) => matchCopyCase(value, `${prefix.toLowerCase()} play`))
    .replace(/\bbidders\b/gi, (value) => matchCopyCase(value, 'players'))
    .replace(/\bbidder\b/gi, (value) => matchCopyCase(value, 'player'))
    .replace(/\bbidding\b/gi, (value) => matchCopyCase(value, 'playing'))
    .replace(/\boutbid\b/gi, (value) => matchCopyCase(value, 'surpassed'))
    .replace(/\bbids\b/gi, (value) => matchCopyCase(value, 'entries'))
    .replace(/\bbid\b/gi, (value) => matchCopyCase(value, 'entry'));

// This legacy server error describes Standard entry numbers. Match only that
// contract; token amounts in wallet errors and Reveal offers remain monetary.
const SERVER_ENTRY_NUMBER = '([+-]?(?:\\d+(?:\\.\\d*)?|\\.\\d+)(?:e[+-]?\\d+)?)';
const STANDARD_MINIMUM_ERROR = new RegExp(
  `^Minimum bid is ${SERVER_ENTRY_NUMBER} tokens\\. Current highest bid is ${SERVER_ENTRY_NUMBER} tokens \\(\\+ ${SERVER_ENTRY_NUMBER} token increment\\)\\.$`,
  'i'
);

export const formatGameEntryError = (text: string, gameType?: string | null): string => {
  const minimum = gameType === 'standard' ? text.match(STANDARD_MINIMUM_ERROR) : null;
  if (minimum) {
    return `Choose an entry number of at least ${minimum[1]}. Current highest number: ${minimum[2]}; number step: ${minimum[3]}.`;
  }
  return formatGameARamaText(text);
};

export type OneOnOneCategoryStatus = 'pending' | 'approved' | 'rejected' | 'blocked' | 'deleted';

export interface OneOnOneServiceCategory {
  id: number;
  whitelabel_id: number;
  value: string;
  label: string;
  status: OneOnOneCategoryStatus;
  source: 'system' | 'creator' | 'admin' | string;
  is_system: boolean;
  created_by_user_id?: number | null;
  reviewed_by_admin_id?: number | null;
  reviewed_at?: string | null;
  admin_notes?: string | null;
  created_at: string;
  updated_at: string;
}

export interface OneOnOneServiceOption {
  value: string;
  label: string;
  dateCategory: string;
  customServiceLabel?: string;
  isManualCustom?: boolean;
}

export const ONE_ON_ONE_SYSTEM_SERVICE_OPTIONS: OneOnOneServiceOption[] = [
  { value: 'pet_sitting', label: 'Pet Sitting' },
  { value: 'life_coaching', label: 'Life Coaching' },
  { value: 'cooking_lessons', label: 'Cooking Lessons' },
  { value: 'social_coaching', label: 'Social Coaching' },
  { value: 'tutoring', label: 'Tutoring' },
  { value: 'house_cleaning', label: 'House Cleaning' },
].map((option) => ({ ...option, dateCategory: option.value }));

export const ONE_ON_ONE_MANUAL_CUSTOM_OPTION: OneOnOneServiceOption = {
  value: 'custom',
  label: 'Custom Service',
  dateCategory: 'custom',
  isManualCustom: true,
};

export const ONE_ON_ONE_SERVICE_OPTIONS: OneOnOneServiceOption[] = [
  ...ONE_ON_ONE_SYSTEM_SERVICE_OPTIONS,
  ONE_ON_ONE_MANUAL_CUSTOM_OPTION,
];

const DELIVERY_TYPE_LABELS: Record<string, string> = {
  physical: 'Physical Product',
  digital: 'Digital Product',
  dating: ONE_ON_ONE_LABEL,
  dating_date: ONE_ON_ONE_LABEL,
  dating_date_virtual: `${ONE_ON_ONE_LABEL} (Virtual)`,
  dating_date_in_person: `${ONE_ON_ONE_LABEL} (In Person)`,
};

const ONE_ON_ONE_CATEGORY_LABELS: Record<string, string> = {
  pet_sitting: 'Pet Sitting',
  life_coaching: 'Life Coaching',
  cooking_lessons: 'Cooking Lessons',
  social_coaching: 'Social Coaching',
  tutoring: 'Tutoring',
  house_cleaning: 'House Cleaning',
  custom: 'Custom Service',
};

const ONE_ON_ONE_LEGACY_CATEGORY_VALUES = new Set([
  'dinner_date',
  'meet_and_greet',
  'intimate_date',
  'experience',
]);

export const titleCaseLabel = (value?: string | null) =>
  value ? value.replace(/_/g, ' ').replace(/\b\w/g, (char) => char.toUpperCase()) : undefined;

export const isOneOnOneDelivery = (deliveryType?: string | null) =>
  deliveryType === 'dating' || deliveryType === 'dating_date';

export const formatAuctionDeliveryType = (deliveryType?: string | null) => {
  if (!deliveryType) return undefined;
  return DELIVERY_TYPE_LABELS[deliveryType] || titleCaseLabel(deliveryType);
};

export const buildOneOnOneServiceOptions = (
  categories?: OneOnOneServiceCategory[] | null
): OneOnOneServiceOption[] => {
  const options = new Map<string, OneOnOneServiceOption>();

  ONE_ON_ONE_SYSTEM_SERVICE_OPTIONS.forEach((option) => {
    options.set(option.value, option);
  });

  (categories || [])
    .filter((category) => category.status === 'approved')
    .forEach((category) => {
      if (ONE_ON_ONE_CATEGORY_LABELS[category.value]) {
        options.set(category.value, {
          value: category.value,
          label: category.label,
          dateCategory: category.value,
        });
        return;
      }

      options.set(`custom:${category.id}`, {
        value: `custom:${category.id}`,
        label: category.label,
        dateCategory: 'custom',
        customServiceLabel: category.label,
      });
    });

  return [...options.values(), ONE_ON_ONE_MANUAL_CUSTOM_OPTION];
};

export const formatOneOnOneCategory = (category?: string | null, customServiceLabel?: string | null) => {
  if (customServiceLabel?.trim()) return customServiceLabel.trim();
  if (!category) return undefined;
  if (ONE_ON_ONE_LEGACY_CATEGORY_VALUES.has(category)) return 'Creator Services';
  return ONE_ON_ONE_CATEGORY_LABELS[category] || 'Creator Services';
};

export const formatAuctionType = (auctionType?: string | null) => {
  const labels: Record<string, string> = {
    standard: 'Standard',
    luba: 'LUN',
    huba: 'HUN',
    reveal_price: 'Reveal',
    buy_now: 'Archived Purchase',
    dating: ONE_ON_ONE_LABEL,
    dating_date: ONE_ON_ONE_LABEL,
  };
  return auctionType && Object.prototype.hasOwnProperty.call(labels, auctionType)
    ? labels[auctionType]
    : 'Unknown Type';
};

export const getWinningBidPresentation = (gameType?: string | null, purchaseType?: string | null) => {
  const isPurchase = purchaseType === 'buy_now' || gameType === 'buy_now';
  const isNumber = !isPurchase && ['standard', 'luba', 'huba'].includes(gameType || '');
  return {
    label: isPurchase ? 'Purchase Price' : isNumber ? 'Winning Number' : gameType === 'reveal_price' ? 'Winning Amount' : 'Winning Entry',
    unit: isNumber ? '' : ' tokens',
  };
};
